import { useRef, useState } from "react";
import { HiOutlineX } from "react-icons/hi";

// Chip/tag-style multi-select: selected options render as blue pill chips
// (2-per-row grid) with an "x" to remove each one, followed inline by a
// plain text input (no separate search box) that filters the scrollable
// list of remaining not-yet-selected options below.
//
// Deliberately has no "Select All / Unselect All" — callers that need bulk
// selection semantics (orphan cleanup on remove, etc.) wire that through
// onAdd/onRemove instead, one id at a time.
function ChipMultiSelect({
 options,
 selectedIds,
 onAdd,
 onRemove,
 placeholder = "Search…",
 emptyMessage = "No options available",
 getLabel = (o) => o.name,
 getSearchText = getLabel,
 maxVisibleChips = 8,
 disabled = false,
}) {
 const [search, setSearch] = useState("");
 const [visibleCount, setVisibleCount] = useState(20);
 const [open, setOpen] = useState(false);
 const [chipsExpanded, setChipsExpanded] = useState(false);
 const searchInputRef = useRef(null);

 const selected = options.filter((o) => selectedIds.includes(o.id));
 const unselected = options.filter((o) => !selectedIds.includes(o.id));
 const filtered = unselected.filter((o) =>
  getSearchText(o).toLowerCase().includes(search.toLowerCase())
 );
 const visible = filtered.slice(0, visibleCount);

 // Selected chips are collapsed past maxVisibleChips (a large selection would
 // otherwise wrap the input into a multi-line block) — a trailing "+N more"
 // chip expands the rest, and "Show less" re-collapses.
 const hiddenChipCount = selected.length - maxVisibleChips;
 const chipsToShow = chipsExpanded || hiddenChipCount <= 0 ? selected : selected.slice(0, maxVisibleChips);

 // Options are clicked with onMouseDown+preventDefault (not onClick) so the
 // input never blurs first — a plain onClick would close the dropdown before
 // the click could register.
 const selectOption = (id) => (e) => { e.preventDefault(); onAdd(id); };

 return (
 <div className="relative">
  <div
  className={`border border-[#d0d5dd] rounded-md bg-white px-3 py-1.5 min-h-[42px] flex flex-wrap gap-1.5 items-center ${disabled ? "bg-[#f6f8fa] cursor-not-allowed" : "cursor-text"}`}
  onClick={() => !disabled && searchInputRef.current?.focus()}
  >
  {chipsToShow.map((o) => (
   <span
   key={o.id}
   className="flex items-center gap-2 bg-[#0078d4] text-white text-xs font-medium rounded-full pl-3 pr-1.5 py-1.5 min-w-0 max-w-full"
   >
   <span className="truncate">{getLabel(o)}</span>
   {!disabled && (
   <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={(e) => { e.stopPropagation(); onRemove(o.id); }}
    className="shrink-0 flex items-center justify-center bg-transparent border-none text-white cursor-pointer hover:opacity-80"
    aria-label={`Remove ${getLabel(o)}`}
   >
    <HiOutlineX className="text-sm" />
   </button>
   )}
   </span>
  ))}
  {!disabled && !chipsExpanded && hiddenChipCount > 0 && (
   <button
   type="button"
   onMouseDown={(e) => { e.preventDefault(); setChipsExpanded(true); }}
   className="flex items-center bg-[#eff6ff] text-[#0078d4] text-xs font-medium rounded-full px-3 py-1.5 border border-[#bfdbfe] cursor-pointer hover:bg-[#dbeafe]"
   >
   +{hiddenChipCount} more
   </button>
  )}
  {chipsExpanded && hiddenChipCount > 0 && (
   <button
   type="button"
   onMouseDown={(e) => { e.preventDefault(); setChipsExpanded(false); }}
   className="flex items-center bg-[#f6f8fa] text-[#57606a] text-xs font-medium rounded-full px-3 py-1.5 border border-[#d0d5dd] cursor-pointer hover:bg-[#eaeef2]"
   >
   Show less
   </button>
  )}
  {!disabled && (
  <input
   ref={searchInputRef}
   type="text"
   className="flex-1 min-w-[120px] py-1 text-sm text-[#24292f] bg-transparent border-none outline-none focus:outline-none focus:ring-0 focus:border-none placeholder:text-[#8b949e]"
   style={{ boxShadow: "none" }}
   placeholder={selected.length === 0 ? placeholder : ""}
   value={search}
   onChange={(e) => { setSearch(e.target.value); setVisibleCount(20); }}
   onFocus={() => setOpen(true)}
   onBlur={() => setOpen(false)}
   onKeyDown={(e) => {
    if (e.key === "Backspace" && search === "" && selected.length > 0) {
     onRemove(selected[selected.length - 1].id);
    }
   }}
  />
  )}
  {selected.length === 0 && disabled && (
   <span className="text-sm text-[#8b949e] py-1">None</span>
  )}
  </div>

  {!disabled && open && (
  <div className="absolute z-10 left-0 right-0 border border-[#d0d5dd] rounded-md max-h-40 overflow-y-auto bg-white mt-1 shadow-md">
   {options.length === 0 ? (
    <p className="text-sm text-[#57606a] px-3 py-2">{emptyMessage}</p>
   ) : filtered.length === 0 ? (
    <p className="text-sm text-[#57606a] px-3 py-2">
    {unselected.length === 0 ? "All options selected" : "No matching options"}
    </p>
   ) : (
    <>
    {visible.map((o) => (
     <button
     key={o.id}
     type="button"
     onMouseDown={selectOption(o.id)}
     className="w-full text-left flex items-center px-3 py-1.5 hover:bg-[#f6f8fa] cursor-pointer text-sm text-[#24292f] bg-transparent border-none"
     >
     {getLabel(o)}
     </button>
    ))}
    {filtered.length > visibleCount && (
     <button
     type="button"
     onMouseDown={(e) => { e.preventDefault(); setVisibleCount((n) => n + 20); }}
     className="w-full py-2 text-xs font-medium text-[#0078d4] hover:bg-[#eff6ff] bg-transparent border-none cursor-pointer"
     >
     {filtered.length - visibleCount} more
     </button>
    )}
    </>
   )}
  </div>
  )}
 </div>
 );
}

export default ChipMultiSelect;
